import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScanLine, CheckCircle2, AlertTriangle, RefreshCw, ArrowLeft, Camera } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import MemberAvatar from '@/components/MemberAvatar';

const SCANNER_ELEMENT_ID = 'sll-qr-scanner';

/**
 * AdminCheckInPage — mobile-first QR scanner for admins at events.
 * Scans the QR printed on each attendee ticket, posts to
 * /api/admin/events/check-in, then shows the attendee profile + a
 * confirmation that they have been marked attended and their linked
 * keynotes unlocked.
 *
 * Duplicate scans receive a 409 from the backend and the page shows an
 * "already checked in" state instead of admitting them a second time.
 */
export default function AdminCheckInPage({ user }) {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ data: null, at: 0 });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Guard — non-admins can't see this page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/progress', { replace: true });
    }
  }, [user, navigate]);

  const startScanner = async () => {
    setError(null);
    setResult(null);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID);
      }
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        onScan,
        () => { /* scan miss — silent */ },
      );
      setRunning(true);
    } catch (e) {
      const msg = (e && e.message) || 'Could not start camera. Make sure you granted camera permission.';
      setError(msg);
      setRunning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch { /* ignore */ }
    setRunning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const onScan = async (decoded) => {
    // Debounce repeat reads of the same code (camera fires many times)
    const now = Date.now();
    if (lastScanRef.current.data === decoded && (now - lastScanRef.current.at) < 4000) return;
    lastScanRef.current = { data: decoded, at: now };

    if (submitting) return;
    setSubmitting(true);
    await stopScanner();

    try {
      const res = await fetch('/api/admin/events/check-in', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: decoded }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setResult({ kind: 'duplicate', ...(data.detail || {}) });
        toast.warning(data.detail?.message || 'Already checked in');
      } else if (!res.ok) {
        setError(data.detail || `Scan failed (HTTP ${res.status})`);
        toast.error(data.detail || 'Scan failed');
      } else {
        setResult({ kind: 'ok', ...data });
        toast.success(`${data.attendee?.name || 'Attendee'} checked in`);
        // Light vibration feedback on supported devices
        try { navigator.vibrate?.(150); } catch { /* noop */ }
      }
    } catch (e) {
      setError('Network error during check-in');
      toast.error('Network error');
    }
    setSubmitting(false);
  };

  const scanAgain = () => {
    setResult(null);
    setError(null);
    startScanner();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {user && <Navigation user={user} />}
      <main className="pt-20 lg:pt-24 pb-12 px-3 md:px-6">
        <div className="container mx-auto max-w-2xl">
          <button onClick={() => navigate(-1)} className="text-sm text-brand-navy hover:underline mb-3 inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="mb-5">
            <h1 className="text-2xl md:text-3xl font-bold font-heading flex items-center gap-2">
              <ScanLine className="h-6 w-6 text-rose-600" /> QR Check-In
            </h1>
            <p className="text-xs md:text-sm text-slate-500 mt-1">
              Point your camera at an attendee's ticket QR. They'll be marked attended and any linked keynotes will unlock for them.
            </p>
          </div>

          {/* Scanner viewport */}
          {!result && (
            <Card className="p-3 md:p-4 mb-4" data-testid="qr-scanner-card">
              <div id={SCANNER_ELEMENT_ID} className="bg-black rounded-lg overflow-hidden mx-auto"
                style={{ maxWidth: 360, aspectRatio: '1/1' }} />
              {!running && (
                <div className="text-center mt-4">
                  <Button onClick={startScanner} disabled={submitting}
                    className="bg-brand-navy hover:bg-brand-dark text-white"
                    data-testid="start-scanner-btn">
                    <Camera className="h-4 w-4 mr-1.5" /> Start scanning
                  </Button>
                  <p className="text-[11px] text-slate-400 mt-2">Allow camera permission when prompted. Works best in good light.</p>
                </div>
              )}
              {running && (
                <p className="text-center text-xs text-slate-500 mt-3">Searching for QR…</p>
              )}
              {submitting && (
                <p className="text-center text-xs text-brand-navy font-medium mt-3">Checking in…</p>
              )}
              {error && (
                <div className="mt-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-700 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{typeof error === 'string' ? error : 'Something went wrong'}</span>
                </div>
              )}
            </Card>
          )}

          {/* Result */}
          {result && (
            <Card className={`p-5 md:p-6 border-2 ${
              result.kind === 'ok' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'
            }`} data-testid="qr-result-card">
              <div className="flex items-center gap-2 mb-4">
                {result.kind === 'ok' ? (
                  <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Checked in</Badge>
                ) : (
                  <Badge className="bg-amber-500 text-white"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Already checked in</Badge>
                )}
              </div>

              <div className="flex items-center gap-4 mb-5">
                <MemberAvatar
                  user={{
                    name: result.attendee?.name,
                    profile: { picture: result.attendee?.picture },
                  }}
                  size={80}
                  className="border-4 border-white shadow-md"
                />
                <div className="min-w-0">
                  <p className="font-bold text-lg text-slate-900 truncate" data-testid="checkin-attendee-name">{result.attendee?.name || 'Attendee'}</p>
                  {result.attendee?.job_title && (
                    <p className="text-sm text-slate-600 truncate">{result.attendee.job_title}</p>
                  )}
                  {result.attendee?.company_name && (
                    <p className="text-sm text-slate-500 truncate">{result.attendee.company_name}</p>
                  )}
                </div>
              </div>

              <div className="text-sm text-slate-700 mb-5 space-y-1">
                <p><span className="text-slate-500">Event:</span> <strong>{result.event?.title}</strong></p>
                {result.kind === 'ok' && (result.keynotes_unlocked > 0) && (
                  <p className="text-emerald-700">✓ Unlocked {result.keynotes_unlocked} linked keynote{result.keynotes_unlocked === 1 ? '' : 's'} for them.</p>
                )}
                {result.kind === 'duplicate' && (
                  <p className="text-amber-700">{result.message || 'They were checked in earlier.'} Do not admit twice.</p>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={scanAgain} className="flex-1 bg-brand-navy text-white" data-testid="scan-again-btn">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Scan next attendee
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
